import { useCallback, useEffect, useRef, useState } from "react";
import { trackConversion } from "../lib/ads";
import { useNavigate } from "react-router-dom";
import { Mic, PenLine, LayoutTemplate, Sparkles, FileText, Loader2, Check, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { AiNotice } from "../components/AiNotice";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { IntakePanel } from "../components/resume/IntakePanel";
import { ResumeEditor } from "../components/resume/ResumeEditor";
import { TemplatePicker } from "../components/resume/TemplatePicker";
import { ResumePreview } from "../components/resume/ResumePreview";
import { ResumeReview } from "../components/resume/ResumeReview";

const STEPS = [
  { id: "tell", label: "Build", icon: Mic },
  { id: "edit", label: "Edit", icon: PenLine },
  { id: "design", label: "Design", icon: LayoutTemplate },
];

const hasAny = (d) => !!(d && (d.contact.name || d.summary || d.experience.length || d.certifications.length || d.education.length));

export default function ResumeBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [step, setStep] = useState("tell");
  const [saveState, setSaveState] = useState("saved"); // saved | unsaved | saving | error
  const saving = saveState === "saving";
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(null); // saved review being viewed → preview shows its snapshot
  const snapshot = step === "review" && viewing?.snapshot ? { ...viewing.snapshot, reviewed_at: viewing.reviewed_at } : null;
  const dirty = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => { if (step === "edit" && !sessionStorage.getItem("fp_conv_resume_started")) { sessionStorage.setItem("fp_conv_resume_started", "1"); trackConversion("resume_builder_started"); } }, [step]); // once per browser session, only when the editor is actually entered
  useEffect(() => {
    api.get("/resume").then(({ data }) => { setResume(data); if (hasAny(data.data)) setStep("edit"); }).catch(() => toast.error("Could not load your resume."));
  }, []);

  const persist = useCallback(async (r) => {
    setSaveState("saving");
    try { await api.put("/resume", { template: r.template, data: r.data }); dirty.current = false; setSaveState("saved"); return true; }
    catch { setSaveState("error"); toast.error("Could not save changes — check your connection and press Save."); return false; }
  }, []);
  // Auto-save 1.2 s after the last edit; "Save now" flushes immediately.
  useEffect(() => {
    if (!resume || !dirty.current) return;
    setSaveState("unsaved");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(resume), 1200);
    return () => clearTimeout(saveTimer.current);
  }, [resume, persist]);
  const saveNow = () => { if (!resume) return; clearTimeout(saveTimer.current); persist(resume).then((ok) => ok && toast.success("Resume saved.")); };
  useEffect(() => {
    const warn = (e) => { if (saveState === "unsaved" || saveState === "saving") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [saveState]);

  const setData = useCallback((data) => { dirty.current = true; setResume((r) => ({ ...r, data })); }, []);
  const setTemplate = useCallback((template) => { dirty.current = true; setResume((r) => ({ ...r, template })); }, []);
  const onOrganized = (res) => { dirty.current = false; setResume((r) => ({ ...r, data: res.data, template: res.template })); setStep("edit"); };

  const download = async () => {
    if (!user?.is_premium) { toast.info("PDF download is a Premium feature."); navigate("/pricing"); return; }
    setDownloading(true);
    try {
      const r = await api.post("/resume/pdf", { template: resume.template, data: resume.data }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `${(resume.data.contact.name || "resume").replace(/[^A-Za-z0-9]+/g, "_")}_Resume.pdf` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your resume PDF is downloading.");
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch {} }
      toast.error(formatApiErrorDetail(detail));
    }
    finally { setDownloading(false); }
  };

  if (!resume) return <div className="min-h-screen bg-slate-50"><Navbar /></div>;
  const content = hasAny(resume.data);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3"><FileText className="w-4 h-4 text-primary" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Resume Builder</p></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-2" data-testid="resume-title">A professional resume, without the guesswork.</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">Talk or type. We organize it, polish the wording, and lay it out in a design departments expect. Edit anything, switch designs anytime, download as PDF.</p>
        <AiNotice kind="resume" className="mt-2 max-w-2xl" />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            {step === "review" ? (
              <div className="space-y-5" data-testid="resume-review-view">
                <button type="button" onClick={() => setStep(content ? "edit" : "tell")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="resume-review-back">
                  <ArrowLeft className="w-4 h-4" /> Back to my resume
                </button>
                <ResumeReview isPro={!!user?.is_premium} hasContent={content} onView={setViewing} />
              </div>
            ) : (<>
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="resume-toolbar">
            <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1 overflow-x-auto" data-testid="resume-steps">
              {STEPS.map((s, i) => {
                const on = step === s.id;
                return (
                  <button key={s.id} type="button" onClick={() => setStep(s.id)} className={`flex-1 min-w-max inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${on ? "bg-slate-900 text-white" : "text-slate-900 hover:bg-slate-100"}`} data-testid={`resume-step-${s.id}`}>
                    <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${on ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>{s.label}
                  </button>
                );
              })}
              <button type="button" onClick={saveNow} disabled={saveState === "saving"} title={saveState === "saved" ? "All changes are saved automatically — click to save again" : "Save your resume now"}
                className={`min-w-max inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 transition-colors ${saveState === "saved" ? "text-emerald-700 hover:bg-emerald-50" : saveState === "saving" ? "text-slate-500" : saveState === "error" ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
                data-testid="resume-save-button" data-state={saveState}>
                <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${saveState === "saved" ? "bg-emerald-100 text-emerald-700" : saveState === "error" ? "bg-red-100 text-red-700" : saveState === "unsaved" ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-500"}`}>4</span>
                {saveState === "saving" ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : saveState === "saved" ? <><Check className="w-4 h-4" /> Saved</> : saveState === "error" ? <><Save className="w-4 h-4" /> Retry save</> : <><Save className="w-4 h-4" /> Save</>}
              </button>
            </div>
            <span className="hidden sm:block w-px h-6 bg-slate-200 mx-1" aria-hidden />
            <button type="button" onClick={() => setStep("review")} title="Optional — get AI feedback on your saved or uploaded resume" className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors" data-testid="resume-step-review">
              <Sparkles className="w-4 h-4" /> AI Review{!user?.is_premium && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-slate-950 rounded px-1.5 py-0.5">Premium</span>}
            </button>
            </div>
            {saveState === "unsaved" && <p className="-mt-4 mb-4 text-xs text-amber-700" data-testid="resume-unsaved-note">Unsaved changes — saving automatically in a moment, or press Save.</p>}
            {step === "tell" && <IntakePanel hasContent={content} onOrganized={onOrganized} />}
            {step === "edit" && (content ? <ResumeEditor data={resume.data} onChange={setData} /> : (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="resume-edit-empty">
                <p className="font-heading text-xl text-slate-900">Nothing to edit yet</p>
                <p className="text-sm text-slate-500 mt-1">Start by telling us about yourself — or fill in the fields by hand.</p>
                <div className="flex justify-center gap-3 mt-4">
                  <button onClick={() => setStep("tell")} className="text-sm font-semibold text-primary hover:underline" data-testid="resume-empty-tell">Tell us about you</button>
                  <button onClick={() => setData({ ...resume.data, contact: { ...resume.data.contact, name: user?.name || "" } })} className="text-sm font-semibold text-slate-600 hover:underline" data-testid="resume-empty-manual">Fill in by hand</button>
                </div>
              </div>
            ))}
            {step === "design" && <TemplatePicker templates={resume.templates} value={resume.template} onChange={setTemplate} />}
            </>)}
          </div>
          <ResumePreview data={resume.data} template={resume.template} templates={resume.templates} onTemplate={setTemplate} isPro={!!user?.is_premium} onDownload={download} downloading={downloading} saving={saving} snapshot={snapshot} onExitSnapshot={() => setViewing(null)} />
        </div>
      </div>
    </div>
  );
}
